import React, { useMemo, useState } from "react";
import { Check, X, Volume2, RotateCcw, ArrowRight } from "lucide-react";

/**
 * Shuffle helper (Fisher–Yates, stable per-quiz thanks to useMemo).
 */
const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const TYPE_LABEL = {
  kana2sens: "Lis le kana, choisis le sens",
  sens2kana: "Choisis l'écriture japonaise",
  romaji2sens: "Lis la prononciation, choisis le sens",
  audio2kana: "Écoute et choisis l'écriture correspondante",
  fill_in_blank: "Complète la phrase avec le bon mot",
};

/**
 * LessonQuiz — inline validation quiz.
 * Props:
 *   quiz: { pass_threshold, xp, questions[] }
 *   speak: (text) => void  (from useTTS)
 *   onSuccess: () => Promise<void>  (called once when score >= threshold)
 *   onCancel: () => void
 *   alreadyCompleted?: boolean  — if true, the success screen advertises a review pass (no XP claimed).
 */
export const LessonQuiz = ({ quiz, speak, onSuccess, onCancel, alreadyCompleted = false }) => {
  const threshold = quiz.pass_threshold ?? 0.8;
  // Shuffle question order and choices once per attempt
  const [attempt, setAttempt] = useState(0);
  const questions = useMemo(
    () => shuffle(quiz.questions).map((q) => ({ ...q, choices: shuffle(q.choices) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [attempt, quiz],
  );

  const [idx, setIdx] = useState(0);
  const [picked, setPicked] = useState(null);  // string
  const [answers, setAnswers] = useState([]);  // [{correct: bool}]
  const [submitting, setSubmitting] = useState(false);

  const q = questions[idx];
  const done = idx >= questions.length;
  const score = answers.filter((a) => a.correct).length;
  const total = questions.length;
  const percent = total ? Math.round((score / total) * 100) : 0;
  const passed = score / (total || 1) >= threshold;

  const pick = (choice) => {
    if (picked) return;
    setPicked(choice);
    const correct = choice === q.answer;
    setAnswers((prev) => [...prev, { correct }]);
  };

  const next = () => {
    setPicked(null);
    setIdx((i) => i + 1);
  };

  const retry = () => {
    setAnswers([]);
    setIdx(0);
    setPicked(null);
    setAttempt((n) => n + 1);
  };

  const finish = async () => {
    setSubmitting(true);
    try {
      await onSuccess();
    } finally {
      setSubmitting(false);
    }
  };

  // Vocab hint shown after answering (kana + romaji + sens for the correct answer)
  const buildFeedback = () => {
    if (!q) return null;
    switch (q.type) {
      case "kana2sens":
        return { jp: q.prompt, fr: q.answer };
      case "sens2kana":
        return { jp: q.answer, fr: q.prompt };
      case "romaji2sens":
        return { romaji: q.prompt, fr: q.answer };
      case "audio2kana":
        return { jp: q.answer };
      case "fill_in_blank":
        // Reconstruit la phrase complète avec la bonne réponse à la place du trou.
        return { jp: (q.prompt || "").replace("___", q.answer), fr: q.prompt_hint };
      default:
        return null;
    }
  };

  // ---- Render ----
  if (done) {
    // Result screen
    return (
      <div
        role="dialog"
        aria-modal="true"
        className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
        data-testid="quiz-result"
      >
        <div className="bg-[#FDFBF7] w-full max-w-lg rounded-3xl border-2 border-[#1A2A40] shadow-brut p-8">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#E94A3F] mb-2">
            Résultat du quiz
          </div>
          <h3 className="font-display font-black text-3xl tracking-tighter leading-[1.05] text-[#1A2A40] mb-4">
            {passed ? "Bravo, tu valides la leçon." : "Presque, tu peux réessayer."}
          </h3>
          <div className="mb-6 flex items-center gap-4">
            <div
              className={`text-5xl font-display font-black ${passed ? "text-[#8CA07A]" : "text-[#E94A3F]"}`}
              data-testid="quiz-score"
            >
              {score}/{total}
            </div>
            <div className="text-sm text-[#4A5568]">
              <div><strong>{percent}%</strong> de bonnes réponses</div>
              <div>Seuil de réussite : {Math.round(threshold * 100)}%</div>
            </div>
          </div>
          {passed ? (
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={finish}
                disabled={submitting}
                data-testid="quiz-claim-xp-btn"
                className="flex-1 bg-[#E94A3F] text-white font-bold py-3 rounded-xl shadow-brut-sm hover-lift inline-flex items-center justify-center gap-2 disabled:opacity-60"
              >
                <Check className="w-4 h-4" />{" "}
                {submitting
                  ? "..."
                  : alreadyCompleted
                    ? "Terminer (révision, pas d'XP)"
                    : `Valider (+${quiz.xp ?? 25} XP)`}
              </button>
              <button
                onClick={onCancel}
                data-testid="quiz-close-btn"
                className="sm:w-auto bg-white text-[#1A2A40] font-bold py-3 px-5 rounded-xl border-2 border-[#1A2A40]/20"
              >
                Fermer
              </button>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={retry}
                data-testid="quiz-retry-btn"
                className="flex-1 bg-[#1A2A40] text-white font-bold py-3 rounded-xl shadow-brut-sm hover-lift inline-flex items-center justify-center gap-2"
              >
                <RotateCcw className="w-4 h-4" /> Réessayer
              </button>
              <button
                onClick={onCancel}
                data-testid="quiz-close-btn"
                className="sm:w-auto bg-white text-[#1A2A40] font-bold py-3 px-5 rounded-xl border-2 border-[#1A2A40]/20"
              >
                Revenir à la leçon
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  const isCorrect = picked && picked === q.answer;
  const fb = buildFeedback();

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      data-testid="lesson-quiz"
    >
      <div className="bg-[#FDFBF7] w-full max-w-xl rounded-3xl border-2 border-[#1A2A40] shadow-brut p-8">
        <div className="flex items-center justify-between mb-4">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#E94A3F]">
            Question {idx + 1} / {total}
          </div>
          <button
            onClick={onCancel}
            aria-label="Fermer le quiz"
            data-testid="quiz-cancel-btn"
            className="p-1.5 rounded-md hover:bg-[#1A2A40]/5 text-[#4A5568]"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Progress bar */}
        <div className="h-1.5 bg-[#1A2A40]/10 rounded-full overflow-hidden mb-6">
          <div
            className="h-full bg-[#E94A3F] transition-all"
            style={{ width: `${((idx + (picked ? 1 : 0)) / total) * 100}%` }}
          />
        </div>

        <p className="text-sm text-[#4A5568] mb-2">{q.prompt_hint || TYPE_LABEL[q.type]}</p>

        {/* Prompt */}
        <div className="mb-6" data-testid={`quiz-q-${idx}`}>
          {q.type === "audio2kana" ? (
            <button
              onClick={() => speak(q.prompt)}
              data-testid="quiz-play-audio"
              className="inline-flex items-center gap-3 bg-[#1A2A40] text-white font-bold py-4 px-6 rounded-xl shadow-brut-sm hover-lift"
            >
              <Volume2 className="w-5 h-5" /> Écouter
            </button>
          ) : q.type === "romaji2sens" ? (
            <div className="font-mono text-3xl sm:text-4xl font-bold text-[#E94A3F] tracking-wide">
              {q.prompt}
            </div>
          ) : q.type === "kana2sens" ? (
            <div className="font-jp font-bold text-5xl sm:text-6xl text-[#1A2A40] leading-none">
              {q.prompt}
            </div>
          ) : q.type === "fill_in_blank" ? (
            <div>
              <div className="font-jp font-bold text-2xl sm:text-3xl text-[#1A2A40] leading-snug">
                {q.prompt}
              </div>
              {q.prompt_hint && (
                <div className="text-sm italic text-[#4A5568] mt-2">{q.prompt_hint}</div>
              )}
            </div>
          ) : (
            <div className="font-display font-black text-3xl sm:text-4xl tracking-tighter text-[#1A2A40]">
              {q.prompt}
            </div>
          )}
        </div>

        {/* Choices */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          {q.choices.map((c, i) => {
            const isPicked = picked === c;
            const isAnswer = c === q.answer;
            let cls = "border-[#1A2A40]/15 bg-white hover:border-[#E94A3F]";
            if (picked) {
              if (isAnswer) cls = "border-[#8CA07A] bg-[#8CA07A]/10";
              else if (isPicked) cls = "border-[#E94A3F] bg-[#E94A3F]/10";
              else cls = "border-[#1A2A40]/10 bg-white opacity-60";
            }
            const useJp = q.type === "sens2kana" || q.type === "audio2kana" || q.type === "fill_in_blank";
            return (
              <button
                key={i}
                onClick={() => pick(c)}
                disabled={!!picked}
                data-testid={`quiz-choice-${i}`}
                className={`text-left border-2 rounded-xl px-4 py-3 transition-colors ${cls} ${
                  useJp ? "font-jp font-bold text-2xl text-[#1A2A40]" : "text-[#1A2A40]"
                }`}
              >
                {c}
              </button>
            );
          })}
        </div>

        {/* Feedback */}
        {picked && (
          <div
            data-testid="quiz-feedback"
            className={`rounded-xl p-4 border-2 mb-4 ${
              isCorrect ? "border-[#8CA07A] bg-[#8CA07A]/10" : "border-[#E94A3F] bg-[#E94A3F]/10"
            }`}
          >
            <div className="flex items-center gap-2 text-sm font-bold mb-1">
              {isCorrect ? (
                <><Check className="w-4 h-4 text-[#8CA07A]" /> <span className="text-[#8CA07A]">Bonne réponse !</span></>
              ) : (
                <><X className="w-4 h-4 text-[#E94A3F]" /> <span className="text-[#E94A3F]">Faux.</span></>
              )}
            </div>
            <div className="text-sm text-[#1A2A40] flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span className="text-xs uppercase tracking-widest text-[#4A5568]">Réponse</span>
              {fb?.jp && <span className="font-jp font-bold text-lg">{fb.jp}</span>}
              {fb?.romaji && <span className="font-mono text-xs text-[#E94A3F]">{fb.romaji}</span>}
              {fb?.fr && <span className="text-[#4A5568]">{fb.fr}</span>}
            </div>
          </div>
        )}

        <div className="flex justify-end">
          <button
            onClick={next}
            disabled={!picked}
            data-testid="quiz-next-btn"
            className="bg-[#E94A3F] text-white font-bold py-3 px-5 rounded-xl shadow-brut-sm hover-lift inline-flex items-center gap-2 disabled:opacity-50"
          >
            {idx + 1 === total ? "Voir le résultat" : "Question suivante"} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default LessonQuiz;
